import React from 'react';
import { motion } from 'framer-motion';
import StarCanvas from './StarCanvas';
import { Target, Check } from 'lucide-react';
import { useWaitlist } from '@/context/WaitlistContext';
import { cn } from '@/lib/utils';

// Constants shared with Hero component
const TOTAL_SPOTS = 100;
const DEADLINE = new Date('2024-02-12T23:59:59');

const TimeUnit = ({ value, label }: { value: string, label: string }) => (
  <div className="flex flex-col items-center">
    <span className="font-mono text-lg sm:text-xl font-bold text-red-400">{value}</span>
    <span className="text-[10px] uppercase tracking-wider text-gray-400 mt-0.5">{label}</span>
  </div>
);

const PricingSection = () => {
  const { spotsLeft, timeLeft } = useWaitlist();

  // Split time into units
  const timeUnits = timeLeft.split(':').map((unit, index) => {
    const labels = ['days', 'hrs', 'min', 'sec'];
    return { value: unit, label: labels[index] };
  });

  const features = [
    "1,000 hyper-personalized emails/month",
    "Unlimited leads research",
    "Unlimited enriched profiles",
    "Multi-channel outreach (Email + call)",
    "Full AI automation suite",
    "Done-for-you email setup",
    "Personal domain warm-up",
    "Dedicated support engineer",
    "Advanced AI customization",
    "Direct development team access"
  ];

  const scrollToForm = () => {
    const formElement = document.getElementById('contact-form');
    if (formElement) {
      formElement.scrollIntoView({ behavior: 'smooth' });
    }
  };

  return (
    <section className="relative py-12 md:py-20 overflow-hidden">
      {/* Background */}
      <StarCanvas />

      {/* Grid pattern overlay */}
      <div className="absolute inset-0 bg-[linear-gradient(to_right,#80808012_1px,transparent_1px),linear-gradient(to_bottom,#80808012_1px,transparent_1px)] bg-[size:14px_24px] opacity-50" />

      {/* Main Pricing Card */}
      <div className="max-w-4xl mx-auto px-4 sm:px-6 relative z-10">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="relative rounded-2xl bg-[#131022] border border-purple-500/10 overflow-hidden"
        >
          {/* Time-Sensitive Banner */}
          <div className="bg-[#1a103d] px-4 sm:px-6 py-3 flex flex-col sm:flex-row sm:justify-between sm:items-center gap-2">
            <div className="flex items-center gap-4">
              <div className="flex items-center">
                <span className="w-2 h-2 bg-red-500 rounded-full animate-pulse mr-3" />
                <span className="text-gray-300 text-sm sm:text-base">
                  Limited Time Offer:
                </span>
              </div>
              <div className="flex gap-3 items-center">
                {timeUnits.map((unit, index) => (
                  <React.Fragment key={unit.label}>
                    <TimeUnit value={unit.value} label={unit.label} />
                    {index < timeUnits.length - 1 && (
                      <span className="text-gray-600 font-mono text-lg">:</span>
                    )}
                  </React.Fragment>
                ))}
              </div>
            </div>
            <div className="flex items-center space-x-2">
              <span className="w-2 h-2 bg-purple-400 rounded-full animate-pulse" />
              <span className="text-gray-300 text-sm sm:text-base">
                <span className="text-purple-400 font-bold">{spotsLeft}</span>
              </span>
            </div>
          </div>

          <div className="p-4 sm:p-8 md:p-12">
            {/* Title and Price */}
            <div className="text-center mb-8 sm:mb-12">
              <h2 className="text-xl sm:text-2xl md:text-3xl text-white mb-6 sm:mb-8 px-2">
                Replace Your Entire Sales Team
              </h2>
              <div className="flex items-center justify-center mb-2">
                <span className="text-gray-500 line-through text-lg sm:text-xl">
                  $2,497
                </span>
                <motion.div
                  whileInView={{ scale: [0.95, 1.05, 1] }}
                  transition={{ duration: 1 }}
                  className="relative"
                >
                  <div className="absolute inset-0 bg-purple-500/20 blur-xl rounded-full" />
                  <span className="text-4xl sm:text-5xl font-bold text-white ml-4 relative">
                    $997
                  </span>
                </motion.div>
                <span className="text-gray-400 ml-2">/month</span>
              </div>
              <div className="text-purple-400 text-sm sm:text-base">
                40% Lifetime Discount
              </div>
            </div>

            {/* Expected Results Box */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="bg-purple-500/10 rounded-xl p-4 sm:p-6 border border-purple-500/20 mb-8 sm:mb-12"
            >
              <div className="flex items-start gap-3">
                <Target className="w-5 sm:w-6 h-5 sm:h-6 text-purple-400 mt-1" />
                <div>
                  <h3 className="text-white font-semibold text-base sm:text-lg mb-2">
                    Expected Monthly Results
                  </h3>
                  <p className="text-purple-200 text-base sm:text-lg">
                    Expect 25-40 qualified meetings from your 1,000 emails
                  </p>
                </div>
              </div>
            </motion.div>

            {/* Comparison */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 sm:gap-12 mb-8 sm:mb-12 relative">
              {/* Connector Line */}
              <div className="absolute top-1/2 left-1/2 w-px h-[80%] bg-gradient-to-b from-purple-500/0 via-purple-500/10 to-purple-500/0 -translate-x-1/2 -translate-y-1/2 hidden md:block" />

              {/* Without ACOS */}
              <div className="space-y-4 sm:space-y-6">
                <h3 className="text-white font-semibold mb-4 sm:mb-6">
                  Without ACOS
                </h3>
                {[
                  {
                    text: "160+ hours monthly on manual tasks",
                    sub: "$9,000+ in time costs",
                  },
                  { text: "Multiple tool subscriptions", sub: "$400+ monthly" },
                  { text: "SDR salary & benefits", sub: "$5,500+ monthly" },
                ].map((item, i) => (
                  <motion.div
                    key={i}
                    whileHover={{ x: 5 }}
                    className="flex items-start space-x-3 group"
                  >
                    <span className="w-3 h-3 rounded-full bg-red-500/80 mt-1.5 group-hover:animate-pulse" />
                    <div>
                      <div className="text-gray-200">{item.text}</div>
                      <div className="text-gray-500 text-sm">{item.sub}</div>
                    </div>
                  </motion.div>
                ))}
              </div>

              {/* With ACOS */}
              <div className="space-y-4 sm:space-y-6">
                <h3 className="text-white font-semibold mb-4 sm:mb-6">
                  With ACOS
                </h3>
                {[
                  {
                    text: "24/7 Automated Operations",
                    sub: "100+ leads researched daily",
                  },
                  {
                    text: "All-in-One Solution",
                    sub: "Replace all manual tools",
                  },
                  {
                    text: "3x More Meetings",
                    sub: "40%+ higher response rates",
                  },
                ].map((item, i) => (
                  <motion.div
                    key={i}
                    whileHover={{ x: 5 }}
                    className="flex items-start space-x-3 group"
                  >
                    <span className="w-3 h-3 rounded-full bg-green-500/80 mt-1.5 group-hover:animate-pulse" />
                    <div>
                      <div className="text-gray-200">{item.text}</div>
                      <div className="text-gray-500 text-sm">{item.sub}</div>
                    </div>
                  </motion.div>
                ))}
              </div>
            </div>

            {/* Value Metrics */}
            <div className="bg-[#1E1736] rounded-xl p-4 sm:p-6 mb-8 sm:mb-12">
              <h3 className="text-white font-semibold mb-4 sm:mb-6">
                Monthly Value Delivered
              </h3>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                {[
                  { value: "$9K+", label: "Time Saved" },
                  { value: "$400+", label: "Tools Saved" },
                  { value: "$5.5K+", label: "HR Savings" },
                  { value: "25x", label: "ROI" },
                ].map((metric, i) => (
                  <motion.div
                    key={i}
                    whileHover={{ y: -2 }}
                    className="text-center p-2"
                  >
                    <motion.div
                      whileInView={{ opacity: [0, 1], y: [20, 0] }}
                      transition={{ delay: i * 0.1 }}
                      className="text-xl sm:text-2xl font-bold text-white mb-1"
                    >
                      {metric.value}
                    </motion.div>
                    <div className="text-gray-400 text-xs sm:text-sm">
                      {metric.label}
                    </div>
                  </motion.div>
                ))}
              </div>
            </div>

            {/* Features List */}
            <div className="mb-8 sm:mb-12">
              <h3 className="text-white font-semibold mb-4 sm:mb-6">
                Everything You Get
              </h3>
              <div className="grid gap-3 sm:gap-4">
                {features.map((feature, index) => (
                  <motion.div
                    key={index}
                    initial={{ opacity: 0, x: -20 }}
                    whileInView={{ opacity: 1, x: 0 }}
                    viewport={{ once: true }}
                    transition={{ delay: index * 0.1 }}
                    className="flex items-center gap-2 sm:gap-3"
                  >
                    <Check className="w-4 sm:w-5 h-4 sm:h-5 text-purple-400 flex-shrink-0" />
                    <span className="text-gray-200 text-sm sm:text-base">
                      {feature}
                    </span>
                  </motion.div>
                ))}
              </div>
            </div>

            {/* CTA */}
            <motion.button
              onClick={scrollToForm}
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
              className="w-full bg-purple-600 hover:bg-purple-500 text-white font-semibold py-3 sm:py-4 px-4 rounded-full transition-colors mb-3 sm:mb-4 text-sm sm:text-base"
            >
              {spotsLeft > 0
                ? `Secure Your Spot (${spotsLeft} remaining)`
                : "Join Waitlist"}
            </motion.button>

            <div className="text-center text-gray-500 text-xs sm:text-sm">
              14-day money-back guarantee
            </div>
          </div>
        </motion.div>
      </div>
    </section>
  );
};

export default PricingSection; 